import React, { useState, useEffect } from 'react';
import { trackVisit } from '../utils/tracking';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import toast from 'react-hot-toast';
import { mergeFields } from '../components/hiring/ApplicationFormEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const inputCls = 'w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-emerald-500 focus:outline-none';

function QuestionInput({ q, value, onChange }) {
  const common = { required: !!q.mandatory, value: value ?? '', onChange: e => onChange(e.target.value), className: inputCls, 'data-testid': `apply-screening-${q.id}` };
  if (q.type === 'boolean') return <select {...common}><option value="">Select…</option><option value="true">Yes</option><option value="false">No</option></select>;
  if (q.type === 'select' && q.options?.length) return <select {...common}><option value="">Select…</option>{q.options.map(o => <option key={o} value={o}>{o}</option>)}</select>;
  if (q.type === 'textarea') return <textarea {...common} className={inputCls + ' h-24'} />;
  return <input {...common} type={q.type === 'number' ? 'number' : 'text'} />;
}

export default function JobApply() {
  const { slug } = useParams();
  const [job, setJob] = useState(undefined);
  const [b, setB] = useState({ company_name: 'Samaritan Solar', primary_color: '#10b981', logo_url: '' });
  const [form, setForm] = useState({ name: '', email: '', phone: '', years_experience: '', closing_rate: '', solar_experience: false, door_to_door: false, has_vehicle: false, bilingual: false, full_time: false, cover_note: '', linkedin_url: '', channel: 'careers' });
  const [resume, setResume] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [applied, setApplied] = useState(null);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    trackVisit({ page: 'job', job_slug: slug });
    axios.get(`${API_URL}/api/careers/${slug}`).then(r => setJob(r.data)).catch(() => setJob(null));
    axios.get(`${API_URL}/api/careers/branding`).then(r => setB(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!job || !job.structured_data) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'jobposting-ld';
    el.text = JSON.stringify(job.structured_data);
    document.head.appendChild(el);
    document.title = `${job.title} — Careers`;
    return () => { const ex = document.getElementById('jobposting-ld'); if (ex) ex.remove(); };
  }, [job]);
  const color = b.primary_color || '#10b981';

  const fields = mergeFields(job?.application_fields).filter(f => f.enabled);
  const textFields = fields.filter(f => ['text', 'number', 'url'].includes(f.type));
  const boolFields = fields.filter(f => f.type === 'bool');
  const resumeField = fields.find(f => f.key === 'resume');
  const coverField = fields.find(f => f.key === 'cover_note');

  const pickResume = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please upload a PDF file'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Resume must be under 8 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setResume({ filename: file.name, b64: String(reader.result).split(',')[1], size: file.size });
    reader.readAsDataURL(file);
  };

  const apply = async (e) => {
    e.preventDefault();
    if (resumeField?.mandatory && !resume) { toast.error('Please attach your resume'); return; }
    setSubmitting(true);
    try {
      const screening_answers = (job.screening_questions || []).map(q => ({ question_id: q.id, question: q.question, answer: answers[q.id] ?? '' }));
      const src = new URLSearchParams(window.location.search).get('src');
      const r = await axios.post(`${API_URL}/api/careers/${slug}/apply`, {
        ...form, channel: src || 'careers', screening_answers,
        resume_b64: resume?.b64 || '', resume_filename: resume?.filename || '',
      });
      setApplied(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not submit application'); }
    setSubmitting(false);
  };

  const book = async (slot) => {
    try {
      const r = await axios.post(`${API_URL}/api/careers/book`, { candidate_id: applied.candidate_id, slot_id: slot.id });
      setBooked(r.data);
      toast.success('Interview booked!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not book that time'); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  if (job === undefined) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400"><i className="fa-solid fa-circle-notch fa-spin text-2xl" /></div>;
  if (job === null) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 text-center">
      <div><i className="fa-solid fa-link-slash text-rose-400 text-3xl mb-3 block" /><p className="text-slate-300">This position is no longer accepting applications.</p><Link to="/careers" className="text-emerald-400 text-sm mt-3 inline-block">← View open roles</Link></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="job-apply-page">
      <header className="border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          {b.logo_url ? <img src={b.logo_url} alt={b.company_name} className="h-8" /> : <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: color }}><i className="fa-solid fa-solar-panel text-white text-sm" /></div>}
          <Link to="/careers" className="text-sm text-slate-400 hover:text-white">{b.company_name} Careers</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-black">{job.title}</h1>
        <p className="text-slate-400 mt-1">{job.location || job.market} · {job.employment_type}</p>
        {job.compensation_public?.ote_low ? <p className="text-emerald-400 font-semibold mt-3">OTE ${job.compensation_public.ote_low.toLocaleString()} – ${job.compensation_public.ote_high.toLocaleString()}/yr</p> : null}

        <div className="md-doc-dark mt-6 text-slate-300 text-sm bg-slate-900 border border-slate-800 rounded-2xl p-6" data-testid="apply-job-description"><ReactMarkdown>{job.description || 'Details coming soon — apply now and we will walk you through the role on the intro call.'}</ReactMarkdown></div>

        <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="apply-next-steps">
          {[['fa-paper-plane', '1 · Apply in 2 min', 'Resume + a few quick questions'], ['fa-eye', '2 · Human review', 'Read within 48 hours, AI-scored fairly'], ['fa-handshake', '3 · Interview', 'Meet the team and see the turf app live']].map(([icon, t, d]) => (
            <div key={t} className="bg-slate-900 border border-slate-800 rounded-xl p-4"><i className={`fa-solid ${icon} text-sm`} style={{ color }} /><p className="text-sm font-bold mt-2">{t}</p><p className="text-xs text-slate-400 mt-1">{d}</p></div>
          ))}
        </div>

        {!applied ? (
          <form onSubmit={apply} className="mt-8 bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4" data-testid="apply-form">
            <p className="font-bold text-lg">Apply now</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-xs text-slate-400">Full name<span className="text-rose-400 ml-0.5">*</span>
              <input required placeholder="Full name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls + ' mt-1'} data-testid="apply-name-input" /></label>
              <label className="block text-xs text-slate-400">Email<span className="text-rose-400 ml-0.5">*</span>
              <input required type="email" placeholder="Email *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls + ' mt-1'} data-testid="apply-email-input" /></label>
              {textFields.map(f => (
                <label key={f.key} className="block text-xs text-slate-400">{f.label}{f.mandatory && <span className="text-rose-400 ml-0.5">*</span>}
                <input required={f.mandatory}
                  type={f.type === 'number' ? 'number' : f.type === 'url' ? 'url' : 'text'}
                  min={f.type === 'number' ? '0' : undefined}
                  placeholder={f.label + (f.mandatory ? ' *' : '')}
                  value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  className={inputCls + ' mt-1'} data-testid={`apply-${f.key}-input`} />
                </label>
              ))}
            </div>

            {resumeField && (
              <div>
                <label htmlFor="resume-file" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800 border border-dashed border-slate-600 hover:border-emerald-500 cursor-pointer transition-colors" data-testid="apply-resume-dropzone">
                  <i className={`fa-solid ${resume ? 'fa-file-circle-check text-emerald-400' : 'fa-file-arrow-up text-slate-400'} text-lg`} />
                  <span className="text-sm text-slate-300 flex-1 truncate">
                    {resume ? resume.filename : `${resumeField.label}${resumeField.mandatory ? ' * required' : ' (optional but recommended)'}`}
                  </span>
                  {resume
                    ? <button type="button" onClick={(ev) => { ev.preventDefault(); setResume(null); }} className="text-xs text-rose-400 font-semibold" data-testid="apply-resume-remove">Remove</button>
                    : <span className="text-xs font-bold" style={{ color }}>Browse</span>}
                </label>
                <input id="resume-file" type="file" accept="application/pdf" className="hidden" onChange={e => pickResume(e.target.files?.[0])} data-testid="apply-resume-input" />
              </div>
            )}

            {boolFields.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {boolFields.map(f => (
                  <label key={f.key} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" required={f.mandatory} checked={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.checked })} className="accent-emerald-500 w-4 h-4" data-testid={`apply-${f.key}-check`} />{f.label}{f.mandatory && <span className="text-rose-400">*</span>}</label>
                ))}
              </div>
            )}

            {(job.screening_questions || []).length > 0 && (
              <div className="space-y-3 pt-2">
                <p className="text-sm font-semibold text-slate-200"><i className="fa-solid fa-clipboard-question mr-1.5 text-emerald-400" />A few screening questions</p>
                {job.screening_questions.map(q => (
                  <div key={q.id} data-testid="screening-question">
                    <label className="text-sm text-slate-300 block mb-1">{q.question}{q.mandatory ? <span className="text-rose-400 ml-1">*</span> : <span className="text-slate-500 ml-1 text-xs">(optional)</span>}</label>
                    {q.help_text && <p className="text-xs text-slate-500 mb-1">{q.help_text}</p>}
                    <QuestionInput q={q} value={answers[q.id]} onChange={v => setAnswers({ ...answers, [q.id]: v })} />
                  </div>
                ))}
              </div>
            )}

            {coverField && (
              <textarea required={coverField.mandatory} placeholder={coverField.label + (coverField.mandatory ? ' *' : ' (optional)')} value={form.cover_note} onChange={e => setForm({ ...form, cover_note: e.target.value })} className={inputCls + ' h-24'} data-testid="apply-cover_note-input" />
            )}
            <button type="submit" disabled={submitting} className="w-full py-3.5 rounded-xl text-white font-bold text-sm hover:brightness-110 active:scale-[0.99] transition-all disabled:opacity-60" style={{ background: color }} data-testid="apply-submit-btn">
              {submitting ? <i className="fa-solid fa-circle-notch fa-spin mr-2" /> : <i className="fa-solid fa-paper-plane mr-2" />}Submit Application
            </button>
          </form>
        ) : booked ? (
          <div className="mt-8 bg-slate-900 border border-emerald-500/40 rounded-2xl p-8 text-center" data-testid="booked-confirmation">
            <i className="fa-solid fa-circle-check text-emerald-400 text-4xl mb-3 block" />
            <p className="text-lg font-bold">You're booked!</p>
            <p className="text-slate-400 text-sm mt-1">Interview scheduled for <b className="text-emerald-400">{fmt(booked.start)}</b>. We'll be in touch by email.</p>
          </div>
        ) : (
          <div className="mt-8 bg-slate-900 border border-slate-800 rounded-2xl p-6" data-testid="application-received">
            <i className="fa-solid fa-circle-check text-emerald-400 text-2xl mb-2 block" />
            <p className="text-lg font-bold" data-testid="application-received-message">Your application has been received and is being reviewed.</p>
            {applied.slots?.length > 0 && <p className="text-slate-400 text-sm mt-1 mb-4">If you'd like, pick an interview time that works for you:</p>}
            <div className="grid sm:grid-cols-2 gap-2" data-testid="booking-panel">
              {(applied.slots || []).map(s => (
                <button key={s.id} onClick={() => book(s)} className="text-left bg-slate-800 hover:bg-emerald-600/20 border border-slate-700 hover:border-emerald-500 rounded-xl px-4 py-3 text-sm text-slate-200 transition-all" data-testid="slot-option">
                  <i className="fa-regular fa-clock mr-2 text-emerald-400" />{fmt(s.start)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
